import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import useSWR from "swr";
import moment from "moment";
import axios from "axios";
import Dialog from "rc-dialog";
import cx from "classnames";
import FileSaver from "file-saver";

import { Link } from "react-router";
import { generatePath } from "react-router-dom";
import { useDispatch } from "react-redux";

import * as constants from "../../constants";

import { addGlobalMessage } from "../../actions/globalmessages";

import Overview, { fetcher } from "../../components/Overview/Overview";
import Quickview from "../../components/Quickview";

import EventsComponent from "./components/eventsComponent";
import CoursesDownloads from "./downloads";
import CoursesInvoices from "./invoices";
import CoursesFinalize from "./finalize";
import CertifyStudentsTrainerVersion from "./CertifyStudentsTrainerVersion";

import { WrapInDialogButton } from "../../components/WrapInDialogButton";
import { Messages } from "../../components/Message";
import { CourseStateTooltip, EmployerSpecificTooltip } from "./components/tooltips";
import CancelCourseDialog from "./CancelCourseDialog";
import { NotifyStudentModal } from "../students/NotifyStudentModal";
import { CopyStudentToCourseModal } from "../students/CopyStudentToCourseModal";
import { buildCertificationTree, filterUnrelatedCertifications } from "../../utils/certifications";
import { hasStaffRole, isTrainerContact } from "../../utils/auth";

import { courseStateNames } from "./components/courseState";
import { CourseStudentListDefinitionBase } from "../../components/Overview/Definitions/CourseStudentList";
import { InsureStudentsModal, SetStudentsBusinessSpecificModal } from "../students/StudentUserOptionModals";
import { smoothScrollToTop } from "../../functions.js";
import { __esModule } from "../../../node_modules/react-router-dom/Router";

export default function CourseDetail(props) {
    const dispatch = useDispatch();
    const dropdownRef = useRef(null);
    const toggleRef = useRef(null);

    const [errorMessage, setErrorMessage] = useState(false);
    const [pdfDownloadLoading, setPDFDownloadLoading] = useState(false);

    const [displayCopyStudentToCourse, setDisplayCopyStudentToCourse] = useState(false);
    const [displayNotifyStudents, setDisplayNotifyStudents] = useState(false);
    const [displayInsureStudents, setDisplayInsureStudents] = useState(false);
    const [displayBusinessSpecificStudents, setBusinessSpecificStudents] = useState(false);
    const [notifyStudentModalOpen, setNotifyStudentModalOpen] = useState(false);
    const [shouldRefreshStudents, setShouldRefreshStudents] = useState(false);
    const [finishCourseModalOpen, setFinishCourseModalOpen] = useState(false);
    const [reopenCourseModalOpen, setReopenCourseModalOpen] = useState(false);
    const [cancelCourseModalOpen, setCancelCourseModalOpen] = useState(false);
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
    const [studentsToCertify, setStudentsToCertify] = useState([]);

    const user = props.authentication?.user;
    const userRoles = user?.roles || [];

    const { data: courseDetail, mutate } = useSWR(() => (props?.params?.itemID ? `/v1/courses/${props.params.itemID}/` : false), fetcher);
    const { data: competenceProfile } = useSWR(
        () =>
            courseDetail?.competence_profile
                ? `/v1/competenceprofiles/${courseDetail.competence_profile}/?fields=tags,warn_if_events_are_too_far_apart,uuid,name,course_name,certification_name,repetition,language,min_date,max_date,contains_elearning,allow_employer_specific,certificate_type`
                : false,
        fetcher
    );
    const { data: contact } = useSWR(() => `/v1/users/${courseDetail.contact_user}/?fields=uuid,full_name`, fetcher);
    const { data: studentDetails, mutate: mutateStudents } = useSWR(
        () => (props?.params?.itemID ? `/v1/courses/${props.params.itemID}/students/?limit=10000` : false),
        fetcher
    );

    const { data: endTerms, mutate: endTermsMutate } = useSWR(
        () => (competenceProfile?.uuid ? `/v1/competenceprofiles/${competenceProfile.uuid}/end_terms/` : false),
        fetcher
    );
    const { data: eventDetails, mutate: mutateEvents } = useSWR(() => (itemID ? `/v1/courses/${props.params.itemID}/events/` : false), fetcher);

    const contactName = contact?.full_name || "-";

    const { data: certificates, mutate: mutateCertificates } = useSWR(
        () =>
            props?.params?.itemID
                ? `/v1/certifications/?course=${
                      props.params.itemID
                  }&fields=uuid,physical_pass,state,url,digital_pass_url,date,user,competence_profile,created,printed,printed_by,end_terms${
                      APP_CONFIG.combineCertificates ? "&include_combinable=true" : ""
                  }&pagination=false`
                : false,
        fetcher
    );

    // Check if certificates exists before setting its properties
    if (certificates) {
        certificates.results = certificates;
    }

    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target) && toggleRef.current && !toggleRef.current.contains(event.target)) {
            setStatusDropdownOpen(false);
        }
    };

    const hasAvailableDescendant = (certification) => {
        if (certification.available) return true;

        for (let i = 0; i < certification.children?.length; i++) {
            if (hasAvailableDescendant(certification.children[i])) {
                return true;
            }
        }

        return false;
    };

    useEffect(() => {
        const updateDocumentTitleAndCheckStudents = async () => {
            if (!!courseDetail && courseDetail?.id) {
                document.title = `${courseDetail.id} - NIBHV Portaal`;
            }
        };

        updateDocumentTitleAndCheckStudents();

        return () => {
            document.title = "NIBHV Portaal";
        };
    }, [courseDetail]);

    /**
     * Debug logging to be able to analyze why a certification is (not) available.
     * @param student
     */
    function logAvailableCertifications(student) {
        if (!console || !console.groupCollapsed || !console.table) return;

        console.groupCollapsed(
            `${student.nibhv_number}: ${student.full_name}`,
            `(${student.available_certifications.filter((c) => c.available).length}/${student.available_certifications.length} available)`
        );
        student.available_certifications.forEach((cert) => {
            console.groupCollapsed(`${cert.competence_profile.certification_name} (available: ${cert.available})`);
            console.table([
                {
                    related: cert.related,
                    ...cert.competence_profile
                }
            ]);
            console.groupCollapsed("End terms");
            console.table(
                cert.end_terms?.map((et) => ({
                    name: et.name,
                    is_achieved: et.latest_progress?.is_achieved,
                    course_id: et.latest_progress?.course_id,
                    date: et.latest_progress?.date,
                    expiration_date: et.latest_progress?.expiration_date,
                    status: et.latest_progress?.status_slug
                }))
            );
            console.groupEnd();
            if (cert.reasons) {
                console.groupCollapsed("Reasons why:");
                console.info(cert.reasons);
                console.groupEnd();
            }
            console.groupEnd();
        });
        console.groupEnd();
    }

    useEffect(() => {
        if (studentDetails && competenceProfile) {
            if (hasStaffRole(userRoles)) {
                studentDetails?.results.forEach(logAvailableCertifications);
            }
            const students = studentDetails?.results?.filter((st) => {
                const tree = buildCertificationTree(filterUnrelatedCertifications(competenceProfile, st.available_certifications));

                return tree.some((certification) => hasAvailableDescendant(certification));
            });

            setStudentsToCertify(students);
        }
    }, [studentDetails, competenceProfile]);

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleClickOutside]);

    const filteredColumns = useMemo(() => {
        return CourseStudentListDefinitionBase.columns
            .filter((o) => o.columnIdentifier !== "insured" || courseDetail?.insurance_available)
            .filter((o) => o.columnIdentifier !== "employer_specific" || competenceProfile?.allow_employer_specific)
            .filter((o) => o.columnIdentifier !== "elearning_status" || competenceProfile?.contains_elearning);
    }, [CourseStudentListDefinitionBase.columns, courseDetail?.insurance_available, competenceProfile?.allow_employer_specific, competenceProfile?.contains_elearning]);

    const filteredBulkOptions = useMemo(() => {
        return CourseStudentListDefinitionBase.bulkOptions.filter((o) => o.bulkIdentifier !== "business-specific-students" || competenceProfile?.allow_employer_specific);
    }, [CourseStudentListDefinitionBase.bulkOptions, competenceProfile?.allow_employer_specific]);

    const addGlobalMessageMemo = useCallback(
        (props) => {
            dispatch(addGlobalMessage(props));
        },
        [dispatch]
    );

    const setDisplayCopyStudentToCourseMemo = useCallback(({ items, contextData, onSuccess, refresh }) => {
        setDisplayCopyStudentToCourse({ items, contextData, onSuccess, refresh });
    }, []);

    const setBusinessSpecificStudentsMemo = useCallback(({ items, contextData, onSuccess, refresh }) => {
        setBusinessSpecificStudents({ items, contextData, onSuccess, refresh });
    }, []);

    const setDisplayNotifyStudentsMemo = useCallback(({ items, contextData, onSuccess, refresh, bulkData }) => {
        setDisplayNotifyStudents({ items, contextData, onSuccess, refresh, bulkData });
    }, []);

    const setDisplayInsureStudentsMemo = useCallback(({ items, contextData, onSuccess, refresh, bulkData }) => {
        setDisplayInsureStudents({ items, contextData, onSuccess, refresh, bulkData });
    }, []);

    const selectRowMemo = useCallback((props) => {}, []);

    if (!courseDetail) {
        return null;
    }

    const isCancellable = [constants.COURSE_STATE_OPEN].includes(courseDetail?.state);
    const isEditable = courseDetail?.is_editable;

    const showPassAdminLink = certificates?.results.some((cert) => cert.physical_pass) && hasStaffRole(userRoles);
    const send_progress = courseDetail?.send_progress;

    /**
     * Returns a display value for a competence profile name with it's date range.
     * If this is going to be used in more places we should lift this up and add more
     * arguments to get the exact format we want.
     * @param {string} name
     * @param {string} certification_name
     * @param {{name: string, id: string}|null} certificate_type
     * @param {string} [min_date] Min date string
     * @param {string} [max_date] Max date string
     * @param {string} format The date format.
     * @returns {JSX.Element}
     */
    const getCPDisplay = ({ name, certification_name, certificate_type, min_date, max_date, contains_elearning }, format = "D MMM") => {
        const min = min_date ? moment(min_date) : null;
        const max = max_date ? moment(max_date) : null;

        let dateRangeText = "";
        if (min && max) {
            dateRangeText = `tussen ${min.format(format)} en ${max.format(format)}`;
        }
        if (min && !max && min > moment()) {
            const date = contains_elearning ? moment(min).add(28, "days") : min;
            dateRangeText = `vanaf ${date.format(format)}`;
        }
        if (!min && max) {
            dateRangeText = `tot ${max.format(format)}`;
        }

        const typeDisplay = certificate_type ? certificate_type.name : "";

        return (
            <>
                <strong>{certification_name || name}</strong>{" "}
                {(typeDisplay || dateRangeText) && (
                    <span className="lighter">
                        ({typeDisplay}
                        {dateRangeText && `${typeDisplay && ", "}voor cursussen ${dateRangeText}`})
                    </span>
                )}
            </>
        );
    };

    const downloadGradeSheet = async (students) => {
        try {
            const response = await axios({
                url: `/v1/courses/${props.params.itemID}/student_grade/`,
                method: "POST",
                responseType: "blob",
                data: {
                    users: students
                },
                headers: {
                    Accept: "*/*"
                }
            });

            const filename = `${courseDetail?.id}_${courseDetail?.display_name?.replaceAll(" ", "_")}_grade_sheet.pdf`;

            FileSaver.saveAs(response.data, filename, { type: response.headers["content-type"] });
        } catch (error) {
            console.error(error);
        }
    };

    const toggleItem = (e, itemID, currentStatus) => {
        let description = "Automatisch tonen van voortgang is geactiveerd";

        if (currentStatus) {
            // Current status is true, so we toggle to false
            let question = "Weet je zeker dat je de cursistenvoortgang in Mijn NIBHV niet automatisch vrij wilt geven tijdens de cursus?";
            description = "Het automatisch tonen van voortgang is gedeactiveerd";

            if (confirm(question)) {
                axios
                    .patch("/v1/courses/" + itemID + "/", {
                        send_progress: !currentStatus
                    })
                    .then((response) => {
                        props.addGlobalMessage({
                            title: description
                        });
                        mutate(response.data.course);
                    })
                    .catch((error) => {
                        console.log(error);
                    });
            }
        } else {
            axios
                .patch("/v1/courses/" + itemID + "/", {
                    send_progress: !currentStatus
                })
                .then((response) => {
                    props.addGlobalMessage({
                        title: description
                    });
                    mutate(response.data.course);
                })
                .catch((error) => {
                    console.log(error);
                });
        }

        smoothScrollToTop(600);
    };

    const cpDisplay = competenceProfile ? getCPDisplay(competenceProfile) : "-";
    const isNIBHVUser = hasStaffRole(props.authentication?.user?.roles || []);
    const isContact = isTrainerContact(props?.authentication?.user?.roles || []);

    let activateItem = (
        <i
            className="fa fa-toggle-off"
            title="Activeren"
            onClick={(e) => {
                e.stopPropagation();
                toggleItem(e, props.params.itemID, send_progress);
            }}
        ></i>
    );
    if (send_progress) {
        activateItem = (
            <i
                className="fa fa-toggle-on"
                title="Deactiveren"
                onClick={(e) => {
                    e.stopPropagation();
                    toggleItem(e, props.params.itemID, send_progress);
                }}
            ></i>
        );
    }

    return (
        <div>
            <div className="section-top">
                <div className="breadcrumbs">
                    <Link to={"/courses"}>Cursussen</Link>
                </div>
                <h1>
                    <strong>
                        {courseDetail.id} - {courseDetail.your_reference || "Laden..."}
                    </strong>
                </h1>
            </div>

            <br />

            <div className="row">
                <div className="col-12">
                    <div className="row">
                        <div className="col-12 flex flex-align-center flex-justify-space-between">
                            <h2>Algemeen</h2>
                            <div className="flex">
                                {isNIBHVUser && (
                                    <WrapInDialogButton
                                        style={{
                                            width: "98%",
                                            height: "100%",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            margin: "70px auto",
                                            padding: 0
                                        }}
                                        buttonTitle="Certificeren"
                                        dialogTitle="Certificeren"
                                        className="btn btn-primary no-sideflap"
                                        noClose={true}
                                        disabledInfoText="Certificering is beschikbaar als er één of meerdere cursisten op de cursus staan met een actieve e-learning of resultaat. Als de cursus is afgerond kunnen er geen certificaten meer worden aangevraagd."
                                        buttonDisabled={
                                            !studentDetails?.results.some((student) => student.available_certifications?.length > 0) ||
                                            !courseDetail.students.length ||
                                            !courseDetail.students.some((student) => student.has_results) ||
                                            courseDetail.state === "finished"
                                        }
                                    >
                                        <CoursesFinalize
                                            {...props}
                                            course={courseDetail}
                                            students={studentDetails}
                                            competenceProfile={competenceProfile}
                                            allowEmployerSpecific={competenceProfile?.allow_employer_specific}
                                            refreshStudents={() => setShouldRefreshStudents((prev) => !prev)}
                                            courseDetailMutate={mutate}
                                            addGlobalMessage={(data) => dispatch(addGlobalMessage(data))}
                                            isPopup={true}
                                        />
                                    </WrapInDialogButton>
                                )}
                                <WrapInDialogButton
                                    buttonTitle="Certificeer cursist(en)"
                                    dialogTitle="Certificeer cursist(en)"
                                    className="btn btn-primary no-sideflap"
                                    style={{
                                        width: "98%",
                                        height: "100%",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        margin: "70px auto",
                                        padding: 0
                                    }}
                                    noClose={true}
                                    disabledInfoText="Deze knop wordt actief zodra er minstens 1 (deel)certificaat in deze cursus aangevraagd kan worden. Als de cursus is afgerond kunnen er geen certificaten meer worden aangevraagd."
                                    buttonDisabled={
                                        isNIBHVUser || isContact
                                            ? false
                                            : !studentsToCertify ||
                                              studentsToCertify.length === 0 ||
                                              courseDetail?.state === "finished" ||
                                              !studentsToCertify.some((student) => !student.cancelled)
                                    }
                                >
                                    <CertifyStudentsTrainerVersion
                                        {...props}
                                        course={courseDetail}
                                        certificates={certificates?.results || []}
                                        students={studentDetails?.results}
                                        competenceProfile={competenceProfile}
                                        refreshStudents={() => setShouldRefreshStudents((prev) => !prev)}
                                        courseDetailMutate={mutate}
                                        allowEmployerSpecific={competenceProfile?.allow_employer_specific}
                                        addGlobalMessage={(data) => dispatch(addGlobalMessage(data))}
                                        isPopup={true}
                                        mutateCertificates={mutateCertificates}
                                    />
                                </WrapInDialogButton>
                                <WrapInDialogButton buttonTitle={"Downloads"} dialogTitle={"Downloads"} className={"btn btn-primary no-sideflap"}>
                                    <CoursesDownloads certificates={certificates?.results} params={props.params} isPopup={true} />
                                </WrapInDialogButton>

                                <WrapInDialogButton buttonTitle={"Facturen"} dialogTitle={"Facturatie"} className={"btn btn-primary no-sideflap"}>
                                    <CoursesInvoices params={props.params} isPopup={true} />
                                </WrapInDialogButton>

                                {/*<WrapInDialogButton*/}
                                {/*    buttonTitle={"Wijzigen"}*/}
                                {/*    dialogTitle={"Wijzigen"}*/}
                                {/*    className={
                                        "btn btn-primary"
                                        + (isCancellable ? " no-sideflap" : "")
                                       }*/}
                                {/*    noClose={true}*/}
                                {/*    onClose={() => mutate() }*/}
                                {/*>*/}
                                {/*    <CoursesGeneral*/}
                                {/*        {...props}*/}
                                {/*        isPopup={true}*/}
                                {/*    />*/}
                                {/*</WrapInDialogButton>*/}

                                {isEditable && (
                                    <Link
                                        to={generatePath("/courses/edit/:uuid/", {
                                            uuid: props.params.itemID
                                        })}
                                    >
                                        <button className="btn btn-primary no-sideflap">Wijzigen</button>
                                    </Link>
                                )}
                                {showPassAdminLink && (
                                    <Link
                                        to={generatePath("/passes/:uuid/", {
                                            uuid: props.params.itemID
                                        })}
                                    >
                                        <button className="btn btn-primary no-sideflap">Passen</button>
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>

                    <br />

                    {errorMessage?.detail && <div className="alert alert-warning">{errorMessage.detail[Object.keys(errorMessage.detail)[0]]}</div>}
                    {[constants.COURSE_STATE_CANCELLED].includes(courseDetail?.state) && (
                        <div className="form-group">
                            <div className="alert alert-info">Deze cursus is geannuleerd.</div>
                        </div>
                    )}

                    <div className="row mb-2">
                        <div className="col-4">Cursus</div>
                        <div className="col-8">
                            <strong>{courseDetail?.display_name}</strong>
                        </div>
                    </div>
                    <div className="row mb-2">
                        <div className="col-4">Competentieprofiel/certificering</div>
                        <div className="col-8">{cpDisplay}</div>
                    </div>
                    <div className="row mb-2">
                        <div className="col-4">Opleider</div>
                        <div className="col-8">{courseDetail?.trainer_details?.name || "-"}</div>
                    </div>
                    <div className="row mb-2">
                        <div className="col-4">Contactpersoon cursus</div>
                        <div className="col-8">{contactName}</div>
                    </div>
                    <div className="row mb-2">
                        <div className="col-4">Referentie</div>
                        <div className="col-8">
                            <strong>{courseDetail?.your_reference}</strong>
                        </div>
                    </div>
                    <div className="row mb-2">
                        <div className="col-4">Toegevoegd / verwacht aantal cursisten</div>
                        <div className="col-8">
                            <strong>
                                {courseDetail?.student_count || 0} / {courseDetail?.expected_students}
                            </strong>
                        </div>
                    </div>
                    <div className="row mb-2">
                        <div className="col-4">E-learning</div>
                        <div className="col-8">{competenceProfile?.contains_elearning ? "Ja" : "Nee"}</div>
                    </div>
                    <div className="row mb-2">
                        <div className="col-4">Cursistopties op deze cursus</div>
                        <div className="col-8">
                            {competenceProfile?.allow_employer_specific && (
                                <>
                                    Bedrijfsspecifiek
                                    <EmployerSpecificTooltip />
                                    <br />
                                </>
                            )}
                            {courseDetail?.insurance_available && (
                                <>
                                    Verzekering
                                    <br />
                                </>
                            )}
                            Vrijstellingen {/* Exemptions are always available. */}
                        </div>
                    </div>

                    <div className="row mb-2">
                        <div className="col-4">
                            Status
                            <CourseStateTooltip />
                        </div>
                        <div className="col-8 flex">
                            <span className={"course-status-indicator course-status-indicator--" + courseDetail?.state}>
                                {courseStateNames?.find((item) => item.slug === courseDetail?.state)?.name}
                            </span>
                            <div className="dropdown-filter-container" style={{ marginLeft: "15px" }}>
                                <div
                                    ref={toggleRef}
                                    className="dropdown-filter-toggle__counter"
                                    style={{ padding: "12px", cursor: "pointer" }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setStatusDropdownOpen((prevOpen) => !prevOpen);
                                    }}
                                >
                                    <i className="fa fa-angle-down" style={{ fontSize: "18px", fontWeight: "bold" }} />
                                </div>
                                <div ref={dropdownRef} style={{ top: "26px" }} className={cx("dropdown-filter", { "dropdown-filter--open": statusDropdownOpen })}>
                                    {/* Option to Finish Course */}
                                    <div
                                        className="dropdown-filter__input"
                                        onClick={() => {
                                            if (
                                                courseDetail?.state !== "finished"
                                                // && certificates?.results?.length > 0
                                            )
                                                setFinishCourseModalOpen(true);
                                            else return;
                                        }}
                                    >
                                        <i
                                            style={{
                                                marginRight: "10px",
                                                backgroundColor: courseDetail?.state !== "finished"
                                                // && certificates?.results?.length > 0 ? "#f60" : "#ccc"
                                            }}
                                            className="fa fa-check save-all-exemptions-icon"
                                        />
                                        <label htmlFor="finished" className="dropdown-filter__label">
                                            <span className="course-status-indicator course-status-indicator--finished">Afgerond</span>
                                        </label>
                                    </div>

                                    {/* Option to Cancel Course */}
                                    <div
                                        className="dropdown-filter__input"
                                        onClick={() => {
                                            if (!["open", "ended"].includes(courseDetail.state)) return;
                                            setCancelCourseModalOpen(true);
                                        }}
                                    >
                                        <i
                                            style={{
                                                marginRight: "10px",
                                                backgroundColor: !["open", "ended"].includes(courseDetail.state) ? "#ccc" : "#f60"
                                            }}
                                            className="fa fa-check save-all-exemptions-icon"
                                        />
                                        <label htmlFor="cancelled" className="dropdown-filter__label">
                                            <span className="course-status-indicator course-status-indicator--cancelled">Geannuleerd</span>
                                        </label>
                                    </div>

                                    {/* Option to Re-Open Course */}
                                    <div
                                        className="dropdown-filter__input"
                                        onClick={() => {
                                            if (["open", "ended"].includes(courseDetail.state)) return;
                                            setReopenCourseModalOpen(true);
                                        }}
                                    >
                                        <i
                                            style={{
                                                marginRight: "10px",
                                                backgroundColor: ["open", "ended"].includes(courseDetail.state) ? "#ccc" : "#f60"
                                            }}
                                            className="fa fa-check save-all-exemptions-icon"
                                        />
                                        <label htmlFor="open" className="dropdown-filter__label">
                                            <span className="course-status-indicator course-status-indicator--open">Open</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <br />

                    <Messages messages={courseDetail.messages} />

                    <EventsComponent competenceProfile={competenceProfile} activateItem={activateItem} {...props} />

                    <div className="course-student-overview-container">
                        <Quickview initialIndex={0}>
                            <Overview
                                classNamePrefix={"overview-redux"}
                                depricateOldStyling={true}
                                quickview={{
                                    display: false,
                                    identifier: "course-student-list-all",
                                    name: "Alles"
                                }}
                                identifier={"course-student-list-all"}
                                identKey={{
                                    courseID: props?.params?.itemID
                                }}
                                definition={{
                                    ...CourseStudentListDefinitionBase,
                                    columns: filteredColumns,
                                    bulkOptions: filteredBulkOptions,
                                    pagination: {
                                        cursorPaginator: {
                                            size: 20,
                                            loadMore: true
                                        }
                                    }
                                }}
                                contextData={{
                                    notifyStudentModalOpen,
                                    setNotifyStudentModalOpen,
                                    // foo: "bar",
                                    studentCount: courseDetail.studentCount,
                                    selectRow: selectRowMemo,
                                    shouldRefreshStudents,
                                    pdfDownloadLoading,
                                    setPDFDownloadLoading,
                                    courseDetail,
                                    studentDetails,
                                    courseDetailMutate: mutate,
                                    downloadGradeSheet,
                                    // endTerms: [...endTerms||[],...endTerms||[], [endTerms?.[0]]], // temp added to be 5
                                    endTerms,
                                    endTermsMutate,
                                    eventDetails,
                                    certificates,
                                    competenceProfile,
                                    // competenceProfileTree,
                                    addGlobalMessage: addGlobalMessageMemo,
                                    setDisplayCopyStudentToCourse: setDisplayCopyStudentToCourseMemo,
                                    setDisplayNotifyStudents: setDisplayNotifyStudentsMemo,
                                    setDisplayInsureStudents: setDisplayInsureStudentsMemo,
                                    setBusinessSpecificStudents: setBusinessSpecificStudentsMemo
                                }}
                            />
                        </Quickview>
                    </div>
                    {displayNotifyStudents && (
                        <NotifyStudentModal
                            items={displayNotifyStudents.items}
                            bulkData={displayNotifyStudents.bulkData}
                            contextData={displayNotifyStudents.contextData}
                            close={() => setDisplayNotifyStudents(false)}
                            onSuccess={() => {
                                displayNotifyStudents?.onSuccess();
                                displayNotifyStudents?.refresh();
                            }}
                            addGlobalMessage={(props) => dispatch(addGlobalMessage(props))}
                        />
                    )}
                    {displayCopyStudentToCourse && (
                        <CopyStudentToCourseModal
                            items={displayCopyStudentToCourse.items}
                            contextData={displayCopyStudentToCourse.contextData}
                            close={() => setDisplayCopyStudentToCourse(false)}
                            onSuccess={() => displayCopyStudentToCourse?.onSuccess()}
                            addGlobalMessage={(props) => dispatch(addGlobalMessage(props))}
                        />
                    )}
                    {displayBusinessSpecificStudents && (
                        <SetStudentsBusinessSpecificModal
                            items={displayBusinessSpecificStudents.items}
                            contextData={displayBusinessSpecificStudents.contextData}
                            close={() => setBusinessSpecificStudents(false)}
                            addGlobalMessage={(props) => dispatch(addGlobalMessage(props))}
                            onSuccess={() => {
                                displayBusinessSpecificStudents?.onSuccess();
                                displayBusinessSpecificStudents?.refresh();
                            }}
                        />
                    )}
                    {displayInsureStudents && (
                        <InsureStudentsModal
                            items={displayInsureStudents.items}
                            contextData={displayInsureStudents.contextData}
                            close={() => setDisplayInsureStudents(false)}
                            addGlobalMessage={(props) => dispatch(addGlobalMessage(props))}
                            onSuccess={() => {
                                displayInsureStudents?.onSuccess();
                                displayInsureStudents?.refresh();
                            }}
                        />
                    )}

                    {cancelCourseModalOpen && (
                        <Dialog title="Cursus annuleren" onClose={() => setCancelCourseModalOpen(false)} visible={cancelCourseModalOpen} animation="fade">
                            <CancelCourseDialog
                                course={courseDetail}
                                users={studentDetails?.results}
                                fallbackCloseDialog={() => setCancelCourseModalOpen(false)}
                                onSuccess={(data) => {
                                    mutate(data.course);
                                    mutateStudents();

                                    const parts = [`De cursus '${courseDetail.your_reference}' is succesvol geannuleerd,`];
                                    if (data.notified > 0) {
                                        parts.push(`${data.notified} ${data.notified === 1 ? "cursist is" : "cursisten zijn"} op de hoogte gebracht van de annulering.`);
                                    }

                                    props.addGlobalMessage({
                                        title: parts.join(" "),
                                        link: "/courses"
                                    });
                                    setCancelCourseModalOpen(false);
                                }}
                                onError={(error) => {
                                    setErrorMessage(error.response?.data);

                                    let messages = "Kan niet annuleren, er zijn al resultaten.";

                                    if (error.response.data && error.response.data?.is_cancelled[0]) {
                                        messages = error.response.data.is_cancelled[0];
                                    }

                                    props.addGlobalMessage({
                                        title: messages,
                                        type: "warning",
                                        icon: "fa fa-info-circle"
                                    });
                                    setCancelCourseModalOpen(false);
                                }}
                            />
                        </Dialog>
                    )}

                    {finishCourseModalOpen && (
                        <Dialog
                            title="Cursus afronden"
                            onClose={() => setFinishCourseModalOpen(false)}
                            visible={finishCourseModalOpen}
                            animation="fade"
                            footer={[
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    key="no"
                                    onClick={() => {
                                        setFinishCourseModalOpen(false);
                                    }}
                                >
                                    Nee
                                </button>,
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    key="yes"
                                    onClick={async () => {
                                        await axios.patch(`/v1/courses/${courseDetail.uuid}/`, { is_finished: true });
                                        dispatch(
                                            addGlobalMessage({
                                                title: "De cursus is gemarkeerd als afronden."
                                            })
                                        );
                                        mutate();
                                        setFinishCourseModalOpen(false);
                                    }}
                                >
                                    Ja
                                </button>
                            ]}
                        >
                            <p className="paragraph">
                                Weet je zeker dat je de cursus wilt afronden? Hierna kan je geen cursisten meer certificeren of cursus gegevens wijzigen.
                            </p>
                        </Dialog>
                    )}

                    {reopenCourseModalOpen && (
                        <Dialog
                            title="Heropen de cursus"
                            onClose={() => setReopenCourseModalOpen(false)}
                            visible={reopenCourseModalOpen}
                            animation="fade"
                            footer={[
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    key="no"
                                    onClick={() => {
                                        setReopenCourseModalOpen(false);
                                    }}
                                >
                                    Nee
                                </button>,
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    key="yes"
                                    onClick={async () => {
                                        await axios.post(`/v1/courses/${courseDetail.uuid}/state/`, { state: "open" });
                                        dispatch(
                                            addGlobalMessage({
                                                title: "De cursus is gemarkeerd als open."
                                            })
                                        );
                                        mutate();
                                        setReopenCourseModalOpen(false);
                                    }}
                                >
                                    Ja
                                </button>
                            ]}
                        >
                            <p className="paragraph">Weet je zeker dat je deze cursus opnieuw wilt openen?</p>
                        </Dialog>
                    )}
                </div>
            </div>
        </div>
    );
}
